import { CloudUploadOutlined } from "@ant-design/icons";
import { Input, Space } from "antd";
import { PageAlert as Alert } from "@/views/components/PageAlert";
import type { OrderFinishForm, OrderVo } from "@zhihaoscm/entity";
import { useEffect, useState } from "react";
import { Button as ZhButton, Dialog as ZhDialog, Upload as ZhUpload, type UploadRequestOptions } from "zhihao-ui-react";

export interface TradeOrderFinishDialogProps {
    order: OrderVo | null;
    mode?: "finish" | "edit";
    initialForm?: OrderFinishForm;
    submitting?: boolean;
    errorMessage?: string | null;
    uploadSettlementFile?: (file: Blob) => Promise<{ id?: number }>;
    onCancel: () => void;
    onConfirm: (form: OrderFinishForm) => void;
}

/** 交易订单完成弹窗，沿用 Vue 的完成提示、吨数和结算凭证字段。 */
export function TradeOrderFinishDialog({
    order,
    mode = "finish",
    initialForm,
    submitting = false,
    errorMessage,
    uploadSettlementFile,
    onCancel,
    onConfirm
}: TradeOrderFinishDialogProps) {
    const [pickedUpTon, setPickedUpTon] = useState("");
    const [settlementIds, setSettlementIds] = useState("");
    const [formError, setFormError] = useState<string | null>(null);
    const [uploadedFileNames, setUploadedFileNames] = useState<string[]>([]);
    const initialSettlementIds = (initialForm?.settlementIds || []).join(", ");

    useEffect(() => {
        if (order === null) {
            setPickedUpTon("");
            setSettlementIds("");
            setUploadedFileNames([]);
            setFormError(null);
        } else if (initialForm) {
            setPickedUpTon(initialForm.pickedUpTon == null ? "" : String(initialForm.pickedUpTon));
            setSettlementIds(initialSettlementIds);
        }
    }, [initialForm?.pickedUpTon, initialSettlementIds, order]);

    /** 复用后台文件服务，上传成功后把文件 ID 加入 Vue 完成订单表单。 */
    const handleUpload = async (options: UploadRequestOptions) => {
        const file = options.file as File;
        if (!uploadSettlementFile) return;
        try {
            const result = await uploadSettlementFile(file);
            if (!result.id) throw new Error("上传响应缺少文件 ID");
            setSettlementIds(current => (current ? `${current},${result.id}` : String(result.id)));
            setUploadedFileNames(current => [...current, file.name]);
            options.onSuccess(result);
        } catch (error) {
            options.onError(error instanceof Error ? error : new Error("结算凭证上传失败"));
        }
    };

    const submit = () => {
        const normalizedTon = pickedUpTon.trim();
        if (normalizedTon && (!Number.isFinite(Number(normalizedTon)) || Number(normalizedTon) <= 0)) {
            setFormError("请输入正确的已提货吨数");
            return;
        }
        setFormError(null);
        const parsedSettlementIds = settlementIds
            .split(",")
            .map(item => Number(item.trim()))
            .filter(value => Number.isInteger(value) && value > 0);
        onConfirm({
            ...(normalizedTon ? { pickedUpTon: Number(normalizedTon) } : {}),
            ...(parsedSettlementIds.length ? { settlementIds: parsedSettlementIds } : {})
        });
    };

    return (
        <ZhDialog
            modalClass="trade-order-finish-dialog"
            open={order !== null}
            title={mode === "edit" ? "修改" : "确认完成"}
            width="600px"
            onCancel={onCancel}
            footer={
                <Space>
                    <ZhButton disabled={submitting} onClick={onCancel}>
                        取消
                    </ZhButton>
                    <ZhButton loading={submitting} type="primary" onClick={submit}>
                        确认
                    </ZhButton>
                </Space>
            }
        >
            <div className="trade-order-finish-dialog-content">
                {errorMessage ? <Alert className="mb-4" message={errorMessage} role="alert" type="error" /> : null}
                {formError ? <Alert className="mb-4" message={formError} role="alert" type="error" /> : null}
                {mode === "finish" ? <div className="mb-4">是否完成，完成后不能再提货</div> : null}
                <div className="trade-order-finish-form-item">
                    <label htmlFor="trade-order-finish-picked-up-ton">已提货吨数</label>
                    <Input
                        id="trade-order-finish-picked-up-ton"
                        aria-label="已提货吨数"
                        min={0.01}
                        placeholder="请输入已提货吨数"
                        suffix="吨"
                        type="number"
                        value={pickedUpTon}
                        onChange={event => setPickedUpTon(event.target.value)}
                    />
                </div>
                <div className="trade-order-finish-form-item trade-order-finish-form-item--file">
                    <div className="trade-order-finish-form-label">
                        <span>结算凭证</span>
                        <small>支持最多上传10个文件，单个文件不超过50M（支持上传压缩包）</small>
                    </div>
                    <div className="trade-order-finish-upload-content">
                        <ZhUpload
                            action="#"
                            aria-label="上传结算凭证"
                            httpRequest={(options: UploadRequestOptions) => void handleUpload(options)}
                            limit={10}
                            showFileList={false}
                        >
                            <div className="trade-order-finish-upload-dropzone">
                                <CloudUploadOutlined />
                                <span>上传文件</span>
                            </div>
                        </ZhUpload>
                        {uploadedFileNames.length ? <div className="mt-2">{uploadedFileNames.join("、")}</div> : null}
                        <Input
                            id="trade-order-finish-settlement-ids"
                            aria-label="结算凭证"
                            className="sr-only"
                            placeholder="请输入附件文件 ID，多个用逗号分隔"
                            value={settlementIds}
                            onChange={event => setSettlementIds(event.target.value)}
                        />
                    </div>
                </div>
            </div>
        </ZhDialog>
    );
}

export default TradeOrderFinishDialog;
